import { CheckCircle, MapPin, NotebookText, Users, Edit2, Trash2 } from 'lucide-react';

interface TalentPackageCardProps {
  packageItem: any;
  isOwnPackage?: boolean;
  hasRequested?: boolean;
  onViewDetails: (packageId: string) => void;
  onSendRequest?: (packageId: string) => void;
  onViewRequests?: (packageId: string, title: string) => void;
  onEdit?: (packageId: string) => void;
  onDelete?: (packageId: string) => void;
}

function formatPriceRange(priceMin?: number | null, priceMax?: number | null, currency?: string | null): string {
  const safeCurrency = currency || 'NOK';
  if (typeof priceMin === 'number' && typeof priceMax === 'number') {
    return `${priceMin} - ${priceMax} ${safeCurrency}`;
  }
  if (typeof priceMin === 'number') {
    return `Fra ${priceMin} ${safeCurrency}`;
  }
  if (typeof priceMax === 'number') {
    return `Inntil ${priceMax} ${safeCurrency}`;
  }
  return 'Pris etter avtale';
}

export function TalentPackageCard({
  packageItem,
  isOwnPackage = false,
  hasRequested = false,
  onViewDetails,
  onSendRequest,
  onViewRequests,
  onEdit,
  onDelete,
}: TalentPackageCardProps) {
  const coverImageUrl = packageItem?.coverImageUrl ?? packageItem?.cover_image_url;
  const imageUrls = packageItem?.imageUrls ?? packageItem?.image_urls;
  const imageUrl = coverImageUrl ?? (Array.isArray(imageUrls) && imageUrls.length > 0 ? imageUrls[0] : packageItem?.imageUrl ?? packageItem?.image_url ?? packageItem?.image);
  const title = packageItem?.title || 'Tjeneste';
  const description = packageItem?.description || '';
  const status = packageItem?.status || 'active';
  const priceLabel = formatPriceRange(
    packageItem?.priceMin ?? packageItem?.price_min,
    packageItem?.priceMax ?? packageItem?.price_max,
    packageItem?.currency
  );

  return (
    <div className="bg-white rounded-xl overflow-hidden hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 border border-gray-200 relative group transform-gpu">
      {/* Edit / Delete overlay – same placement and style as ProjectCardWithActions */}
      {isOwnPackage && (onEdit || onDelete) && (
        <div className="absolute top-2 right-2 z-20 flex gap-1.5 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
          {onEdit && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(packageItem.id);
              }}
              className="p-2 bg-white/95 backdrop-blur-sm rounded-lg shadow-md hover:bg-purple-50 transition-all duration-150 active:scale-95"
              title="Rediger tjeneste"
            >
              <Edit2 className="w-4 h-4 text-purple-600" />
            </button>
          )}
          {onDelete && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(packageItem.id);
              }}
              className="p-2 bg-white/95 backdrop-blur-sm rounded-lg shadow-md hover:bg-red-50 transition-all duration-150 active:scale-95"
              title="Slett tjeneste"
            >
              <Trash2 className="w-4 h-4 text-red-600" />
            </button>
          )}
        </div>
      )}

      {/* Clickable area: image + content – opens detail modal */}
      <div
        className="relative z-0 cursor-pointer"
        onClick={() => onViewDetails(packageItem.id)}
      >
        {/* Image – same aspect ratio as project cards */}
        {imageUrl ? (
          <div className="aspect-[4/3] sm:aspect-video w-full overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200 relative">
            <img
              src={imageUrl}
              alt={title}
              className="w-full h-full object-cover"
              loading="lazy"
            />
            {isOwnPackage && status !== 'active' && (
              <div className="absolute bottom-2 right-2">
                <span className="px-2.5 py-1 bg-amber-500/90 backdrop-blur-sm text-white rounded-lg text-xs font-medium">
                  Ikke publisert
                </span>
              </div>
            )}
          </div>
        ) : (
          <div className="aspect-[4/3] sm:aspect-video w-full overflow-hidden bg-gradient-to-br from-purple-100 to-pink-100 relative flex items-center justify-center">
            <p className="text-sm text-gray-500">Ingen bilde</p>
          </div>
        )}

        {/* Content – same padding and clamp as project cards */}
        <div className="p-3 sm:p-4">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-1.5 line-clamp-1">
            {title}
          </h3>
          <p className="text-sm text-gray-600 mb-3 line-clamp-2">
            {description}
          </p>
          <div className="flex flex-wrap gap-2 text-sm text-gray-600">
            {packageItem?.county && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-gray-100">
                <MapPin className="w-3.5 h-3.5" />
                {packageItem.county}
              </span>
            )}
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-purple-100 text-purple-700">
              <NotebookText className="w-3.5 h-3.5" />
              {priceLabel}
            </span>
          </div>
        </div>
      </div>

      {/* Action row – Forespørsler / Send forespørsel (not part of clickable area) */}
      <div className="px-3 sm:px-4 pb-3 sm:pb-4 flex gap-2 border-t border-gray-100">
        {isOwnPackage && onViewRequests ? (
          <button
            type="button"
            onClick={() => onViewRequests(packageItem.id, title)}
            className="flex-1 px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium inline-flex items-center justify-center gap-2"
          >
            <Users className="w-4 h-4" />
            Forespørsler
          </button>
        ) : hasRequested ? (
          <span className="flex-1 px-3 py-2 text-sm bg-green-100 text-green-700 rounded-lg font-medium inline-flex items-center justify-center gap-2 cursor-default">
            <CheckCircle className="w-4 h-4" />
            Forespørsel sendt
          </span>
        ) : (
          <button
            type="button"
            onClick={() => onSendRequest?.(packageItem.id)}
            className="flex-1 px-3 py-2 text-sm bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:shadow-lg transition-all font-medium"
          >
            Send forespørsel
          </button>
        )}
      </div>
    </div>
  );
}
